<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">资讯管理</h1>
            <p class="list-page-description">管理平台资讯内容，支持分类管理、发布下架、置顶等操作。</p>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <button
              v-permission="'kb:article:edit'"
              @click="openFormModal()"
              class="btn-primary inline-flex items-center gap-2"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              新增资讯
            </button>
          </div>
        </div>
      </div>
    </template>
    <div class="min-h-full overflow-x-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
    <div class="relative overflow-hidden">
      <div class="pointer-events-none absolute -right-40 -top-24 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl"></div>
      <div class="pointer-events-none absolute left-10 top-36 h-40 w-40 rounded-full bg-slate-200/60 blur-3xl"></div>

      <div class="mx-auto w-full px-4 py-6">

        <!-- 主体区域 -->
        <div class="grid grid-cols-12 gap-6">
          <!-- 左侧分类树 -->
          <div class="col-span-12 lg:col-span-3">
            <KbCategoryTree
              :tree="categoryTree"
              :loading="categoryLoading"
              :selected-id="selectedCategoryId"
              
              title="资讯分类"
              description="按分类筛选资讯内容"
              @add="handleAddCategory"
              @edit="handleEditCategory"
              @delete="confirmDeleteCategory"
              @select="handleCategoryTreeSelect"
              @add-child="handleAddChildCategory"
            />
          </div>

          <!-- 右侧内容区 -->
          <div class="col-span-12 lg:col-span-9">
            <!-- 搜索栏 -->
            <PageFilterBar
              body-class="p-4"
              :show-refresh="true"
              :refresh-loading="listLoading"
              @refresh="loadArticleList(pageInfo.current)"
            >
              <div class="relative flex-1 min-w-[200px]">
                <input
                  v-model="searchKeyword"
                  @keyup.enter="loadArticleList(1)"
                  type="text"
                  placeholder="搜索资讯标题或摘要..."
                  class="form-search-input form-control-sm w-full"
                >
                <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <select
                v-model="searchStatus"
                @change="loadArticleList(1)"
                class="form-select-native form-control-sm"
              >
                <option value="">全部状态</option>
                <option value="0">草稿</option>
                <option value="1">已发布</option>
                <option value="2">已下架</option>
              </select>

              <template #searchActions>
                <button
                  @click="loadArticleList(1)"
                  class="btn-primary"
                >搜索</button>
                <button
                  @click="resetFilters"
                  class="btn-secondary"
                >重置</button>
              </template>
            </PageFilterBar>

            <!-- 列表 -->
            <div class="mt-4 list-page-table-panel">
              <!-- 加载骨架屏 -->
              <div v-if="listLoading" class="data-table-shell divide-y divide-slate-100 overflow-hidden">
                <div v-for="n in 5" :key="n" class="animate-pulse p-4">
                  <div class="flex gap-4">
                    <div class="h-20 w-32 shrink-0 rounded-lg bg-slate-200"></div>
                    <div class="flex-1 space-y-2">
                      <div class="h-5 w-3/4 rounded bg-slate-200"></div>
                      <div class="h-4 w-full rounded bg-slate-200"></div>
                      <div class="h-3 w-1/3 rounded bg-slate-200"></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 空状态 -->
              <div v-else-if="!articleList.length" class="data-table-shell flex flex-col items-center justify-center py-20">
                <div class="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <p class="mt-4 text-sm text-slate-500">暂无资讯数据</p>
                <button
                  v-permission="'kb:article:edit'"
                  @click="openFormModal()"
                  class="btn-primary mt-4"
                >新增资讯</button>
              </div>

              <!-- 资讯列表 -->
              <div v-else class="data-table-shell divide-y divide-slate-100 overflow-hidden">
                <div
                  v-for="item in articleList"
                  :key="item.id"
                  class="data-table-row group flex gap-4 p-4 transition-colors"
                >
                  <!-- 封面图 -->
                  <div v-if="item.coverImage" class="h-20 w-32 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                    <img
                      :src="getImageUrl(item.coverImage)"
                      :alt="item.title"
                      class="h-full w-full object-cover"
                    >
                  </div>

                  <!-- 内容 -->
                  <div class="flex min-w-0 flex-1 flex-col justify-between">
                    <div>
                      <div class="flex items-center gap-2">
                        <h3
                          class="data-table-cell-title cursor-pointer truncate hover:text-primary-600"
                          @click="openDetailModal(item)"
                        >{{ item.title }}</h3>
                        <span v-if="item.isTop" class="status-badge shrink-0 bg-amber-50 text-amber-700">置顶</span>
                        <span :class="statusClass(item.status)" class="shrink-0">{{ statusText(item.status) }}</span>
                      </div>
                      <p v-if="item.summary" class="data-table-cell-meta mt-1 line-clamp-1">{{ item.summary }}</p>
                    </div>
                    <div class="data-table-cell-meta mt-2 flex items-center gap-4">
                      <span class="flex items-center gap-1">
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                        {{ getLastCategoryName(item.categoryId) }}
                      </span>
                      <span class="flex items-center gap-1">
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        {{ item.viewCount || 0 }}
                      </span>
                      <span class="flex items-center gap-1">
                        <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        {{ item.createTime || '-' }}
                      </span>
                    </div>
                  </div>

                  <!-- 操作 -->
                  <div class="flex shrink-0 items-center gap-1">
                    <button
                      v-permission="'kb:article:edit'"
                      @click="openFormModal(item)"
                      class="table-action-button is-primary"
                      title="编辑"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button
                      v-permission="'kb:article:edit'"
                      @click="toggleMoreMenu(item.id, $event)"
                      class="more-menu-container table-action-button is-neutral"
                      title="更多"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- 分页 -->
              <AppPagination
                v-if="articleList.length"
                class="pagination-bridge"
                :pagination="pageInfo"
                :size="pageSize"
                :page-sizes="[10, 20, 50]"
                @page-change="handlePageChange"
                @size-change="handlePageSizeChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分类表单弹窗 -->
    <Transition name="modal">
      <div v-if="showCategoryModal" class="fixed inset-0 z-50">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeCategoryModal"></div>
        <div class="relative mx-auto flex min-h-screen max-w-md items-center px-4 py-8">
          <div class="w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
            <!-- 弹窗头部 -->
            <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
              <div class="absolute inset-0 bg-grid-white/10"></div>
              <div class="relative flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white">{{ isCategoryEdit ? '编辑分类' : '新增分类' }}</h3>
                    <p class="mt-0.5 text-sm text-primary-100">资讯分类管理</p>
                  </div>
                </div>
                <button @click="closeCategoryModal" class="cursor-pointer rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <!-- 基础信息 -->
            <div class="space-y-5 px-6 py-6">
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  分类名称 <span class="text-red-500">*</span>
                </label>
                <input v-model="categoryForm.name" class="form-input" placeholder="请输入分类名称">
              </div>
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  上级分类
                </label>
                <select v-model="categoryForm.parentId" class="form-select-native">
                  <option :value="0">顶级分类</option>
                  <option v-for="cat in flatCategories" :key="cat.id" :value="cat.id">{{ cat.label }}</option>
                </select>
              </div>
            </div>

            <!-- 分隔线 -->
            <div class="mx-6 border-t border-dashed border-slate-200"></div>

            <!-- 可选信息 -->
            <div class="space-y-5 px-6 py-6">
              <p class="text-xs font-medium text-slate-400 uppercase tracking-wider">可选信息</p>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                    排序号
                  </label>
                  <input v-model.number="categoryForm.sort" type="number" class="form-input" placeholder="0">
                </div>
                <div class="space-y-1.5">
                  <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                    <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    状态
                  </label>
                  <select v-model.number="categoryForm.status" class="form-select-native">
                    <option :value="0">启用</option>
                    <option :value="1">停用</option>
                  </select>
                </div>
              </div>
              <div class="space-y-1.5">
                <label class="flex items-center gap-1 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                  备注
                </label>
                <textarea v-model="categoryForm.remark" rows="2" class="form-textarea" placeholder="可选备注信息"></textarea>
                <p class="form-helper">填写备注有助于分类管理</p>
              </div>
            </div>

            <!-- 底部按钮 -->
            <div class="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button @click="closeCategoryModal" class="btn-secondary">取消</button>
              <button @click="submitCategory" class="btn-primary hover:shadow-md hover:shadow-primary-500/30">
                {{ isCategoryEdit ? '保存修改' : '创建分类' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 资讯表单弹窗 -->
    <div v-if="showFormModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeFormModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <!-- 弹窗头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5 shrink-0">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white">{{ isEdit ? '编辑资讯' : '新增资讯' }}</h3>
                  <p class="mt-0.5 text-sm text-primary-100">{{ isEdit ? '修改资讯内容' : '创建新的资讯内容' }}</p>
                </div>
              </div>
              <button @click="closeFormModal" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 表单内容 -->
          <div class="overflow-y-auto flex-1 px-6 py-6">
            <div class="space-y-5">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="text-sm font-medium text-slate-700">资讯标题 <span class="text-red-500">*</span></label>
                  <input v-model="articleForm.title" class="form-input" placeholder="请输入资讯标题">
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm font-medium text-slate-700">所属分类</label>
                  <select v-model="articleForm.categoryId" class="form-select-native">
                    <option value="">请选择分类</option>
                    <option v-for="cat in flatCategories" :key="cat.id" :value="cat.id">{{ cat.label }}</option>
                  </select>
                </div>
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-medium text-slate-700">作者</label>
                <input v-model="articleForm.author" class="form-input" placeholder="请输入文章作者">
              </div>
              <div class="space-y-1.5">
                <label class="text-sm font-medium text-slate-700">摘要</label>
                <textarea v-model="articleForm.summary" rows="2" class="form-textarea" placeholder="简短描述资讯内容，用于列表展示"></textarea>
              </div>
              <!-- 封面图 -->
              <div class="space-y-1.5">
                <label class="text-sm font-medium text-slate-700">封面图</label>
                <div class="flex items-start gap-4">
                  <div
                    v-if="coverDisplayUrl"
                    class="group relative h-24 w-40 overflow-hidden rounded-lg border border-slate-200"
                  >
                    <img :src="coverDisplayUrl" class="h-full w-full object-cover">
                    <div class="absolute inset-0 flex items-center justify-center gap-3 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        @click="showCoverPreview = true"
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/40"
                        title="查看大图"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
                      </button>
                      <button
                        @click="removeCover"
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-red-500/80"
                        title="删除封面"
                      >
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                  <button
                    v-if="!coverDisplayUrl"
                    @click="triggerCoverUpload"
                    class="flex h-24 w-40 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-slate-200 text-slate-400 transition-colors hover:border-primary-300 hover:text-primary-500"
                  >
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <span class="text-xs">上传封面</span>
                  </button>
                  <input
                    ref="coverInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleCoverUpload"
                  >
                </div>
              </div>
              <!-- 富文本内容 -->
              <div class="space-y-1.5">
                <label class="text-sm font-medium text-slate-700">资讯内容 <span class="text-red-500">*</span></label>
                <RichTextEditor v-model="articleForm.content" placeholder="请输入资讯内容..." min-height="300px" upload-module="article" />
              </div>
            </div>
          </div>
          <!-- 底部按钮 -->
          <div class="flex justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4 shrink-0">
            <button @click="closeFormModal" class="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900">取消</button>
            <button @click="submitArticle" class="cursor-pointer rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-primary-600/30 transition-all hover:bg-primary-700 hover:shadow-md hover:shadow-primary-600/40">保存</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 资讯详情弹窗 -->
    <div v-if="showDetailModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showDetailModal = false"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-3xl w-full max-h-[90vh] overflow-hidden">
          <!-- 弹窗头部 -->
          <div class="relative bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-5">
            <div class="absolute inset-0 bg-grid-white/10"></div>
            <div class="relative flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-white">资讯详情</h3>
                  <p class="mt-0.5 text-sm text-primary-100">查看资讯详细内容</p>
                </div>
              </div>
              <button @click="showDetailModal = false" class="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/20 hover:text-white">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <!-- 内容区域 -->
          <div class="overflow-y-auto max-h-[calc(90vh-100px)] px-6 py-6">
            <div v-if="detailLoading" class="space-y-4 animate-pulse">
              <div class="h-8 w-3/4 rounded-lg bg-slate-200"></div>
              <div class="h-5 w-1/2 rounded bg-slate-200"></div>
              <div class="h-40 rounded-xl bg-slate-200"></div>
            </div>
            <template v-else-if="detailData">
              <!-- 封面图 -->
              <div v-if="detailData.coverImage" class="mb-5 overflow-hidden rounded-xl">
                <img :src="getImageUrl(detailData.coverImage)" :alt="detailData.title" class="w-full max-h-64 object-cover">
              </div>
              <!-- 状态和元信息 -->
              <div class="flex flex-wrap items-center gap-3">
                <span :class="statusClass(detailData.status)">{{ statusText(detailData.status) }}</span>
                <span v-if="detailData.isTop" class="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                  <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  置顶
                </span>
              </div>
              <!-- 标题 -->
              <h2 class="mt-4 text-xl font-bold text-slate-900">{{ detailData.title }}</h2>
              <!-- 摘要 -->
              <p v-if="detailData.summary" class="mt-2 text-sm text-slate-600 leading-relaxed">{{ detailData.summary }}</p>
              <!-- 统计信息 -->
              <div class="mt-4 flex items-center gap-4 text-xs text-slate-500">
                <div class="flex items-center gap-1.5">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  浏览 {{ detailData.viewCount || 0 }} 次
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  {{ getCategoryName(detailData.categoryId) }}
                </div>
              </div>
              <!-- 内容 -->
              <div class="mt-6">
                <div class="flex items-center gap-2 text-sm font-medium text-slate-900 mb-3">
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                  </div>
                  资讯内容
                </div>
                <div class="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
                  <div class="prose prose-sm max-w-none text-slate-700" v-html="detailData.content"></div>
                </div>
              </div>
              <!-- 元信息 -->
              <div class="mt-6 flex flex-wrap items-center gap-4 rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-3 text-xs text-slate-500">
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  创建者：{{ detailData.createBy || '-' }}
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  创建：{{ detailData.createTime || '-' }}
                </div>
                <div class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  更新：{{ detailData.updateTime || '-' }}
                </div>
                <div v-if="detailData.publishTime" class="flex items-center gap-1.5">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  发布：{{ detailData.publishTime }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 封面图全屏预览 -->
    <div v-if="showCoverPreview" class="fixed inset-0 z-[60] flex items-center justify-center" @click="showCoverPreview = false">
      <div class="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
      <div class="relative max-h-[90vh] max-w-[90vw]">
        <img :src="coverDisplayUrl" class="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl" @click.stop>
        <button
          @click="showCoverPreview = false"
          class="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg transition-colors hover:bg-slate-100"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
    </div>

    <!-- 确认删除弹窗 -->
    <div v-if="showConfirmModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showConfirmModal = false"></div>
      <div class="relative mx-auto flex min-h-screen max-w-sm items-center px-4 py-8">
        <div class="w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          <div class="flex flex-col items-center px-6 pt-8 pb-4">
            <div class="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
              <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 class="mt-4 text-lg font-semibold text-slate-900">确认删除</h3>
            <p class="mt-2 text-center text-sm text-slate-600 leading-relaxed">{{ confirmMessage }}</p>
          </div>
          <div class="flex gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
            <button @click="showConfirmModal = false" class="flex-1 cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900">取消</button>
            <button @click="confirmDeleteAction" class="flex-1 cursor-pointer rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm shadow-red-600/30 transition-all hover:bg-red-700 hover:shadow-md hover:shadow-red-600/40">删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 更多操作下拉菜单 -->
    <Teleport to="body">
      <div
        v-if="openMoreId !== null"
        class="fixed z-[9999] w-40 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl ring-1 ring-black/5"
        :style="moreMenuStyle"
        @click.stop
      >
        <button
          v-if="openMoreItem?.status === 0"
          v-permission="'kb:article:edit'"
          @click="handlePublish(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          发布
        </button>
        <button
          v-if="openMoreItem?.status === 1"
          v-permission="'kb:article:edit'"
          @click="handleOffline(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
          下架
        </button>
        <button
          v-if="openMoreItem?.status === 2"
          v-permission="'kb:article:edit'"
          @click="handleOnline(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          上架
        </button>
        <button
          v-permission="'kb:article:edit'"
          @click="handleToggleTop(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg class="h-4 w-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
          {{ openMoreItem?.isTop ? '取消置顶' : '置顶' }}
        </button>
        <div class="my-1 border-t border-slate-100"></div>
        <button
          v-permission="'kb:article:edit'"
          @click="confirmDeleteArticle(openMoreItem)"
          class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-xs text-red-600 transition-colors hover:bg-red-50"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          删除
        </button>
      </div>
    </Teleport>
    </div>
  </ListPageShell>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import KbCategoryTree from './components/KbCategoryTree.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import AppPagination from '@/components/AppPagination.vue'
import { uploadFile } from '@/api/file'
import { useKbCategoryManagement } from './composables/useKbCategoryManagement'
import {
  getArticleCategoryTree, createArticleCategory, updateArticleCategory, deleteArticleCategory,
  getArticlePage, getArticleDetail, createArticle, updateArticle, deleteArticle,
  publishArticle, offlineArticle, onlineArticle, toggleArticleTop
} from '@/api/kb'

// ==================== 分类相关 ====================
const categoryState = useKbCategoryManagement({
  loadCategoryTree: getArticleCategoryTree,
  createCategory: createArticleCategory,
  updateCategory: updateArticleCategory,
  defaultStatus: 0,
  includeRemark: true,
  messages: {
    loadError: '\u52a0\u8f7d\u5206\u7c7b\u5931\u8d25',
    emptyNameWarning: '\u8bf7\u8f93\u5165\u5206\u7c7b\u540d\u79f0',
    updateSuccess: '\u5206\u7c7b\u66f4\u65b0\u6210\u529f',
    createSuccess: '\u5206\u7c7b\u521b\u5efa\u6210\u529f',
    saveError: '\u4fdd\u5b58\u5206\u7c7b\u5931\u8d25'
  }
})
const categoryTree = categoryState.categoryTree
const categoryLoading = categoryState.categoryLoading
const selectedCategoryId = categoryState.selectedCategoryId
const showCategoryModal = categoryState.showCategoryModal
const isCategoryEdit = categoryState.isCategoryEdit
const categoryForm = categoryState.categoryForm
const loadCategories = categoryState.loadCategories
const flatCategories = categoryState.flatCategories
const getCategoryName = categoryState.getCategoryName
const getLastCategoryName = categoryState.getLastCategoryName
const handleCategorySelect = categoryState.handleCategorySelect
const handleAddCategory = categoryState.handleAddCategory
const handleAddChildCategory = categoryState.handleAddChildCategory
const handleEditCategory = categoryState.handleEditCategory
const closeCategoryModal = categoryState.closeCategoryModal
const submitCategory = categoryState.submitCategory

const articleList = ref([])
const listLoading = ref(false)
const searchKeyword = ref('')
const searchStatus = ref('')
const pageSize = ref(20)
const pageInfo = reactive({ current: 1, total: 0, hasPrevious: false, hasNext: false })

const loadArticleList = async (page = 1) => {
  listLoading.value = true
  try {
    const params = {
      page,
      size: pageSize.value,
      categoryId: selectedCategoryId.value || undefined,
      keyword: searchKeyword.value || undefined,
      status: searchStatus.value === '' ? undefined : Number(searchStatus.value)
    }
    const data = await getArticlePage(params)
    articleList.value = data.records || []
    Object.assign(pageInfo, {
      current: data.current || 1,
      total: data.total || 0,
      hasPrevious: data.hasPrevious,
      hasNext: data.hasNext
    })
  } catch (error) {
    ElMessage.error(error.message || '加载资讯列表失败')
  } finally {
    listLoading.value = false
  }
}



const handleCategoryTreeSelect = (id) => {
  handleCategorySelect(id, () => loadArticleList(1))
}

const handlePageChange = (page) => { loadArticleList(page) }
const handlePageSizeChange = (size) => { pageSize.value = size; loadArticleList(1) }

const statusText = (status) => ({ 0: '草稿', 1: '已发布', 2: '已下架' }[status] || '未知')
const statusClass = (status) => ({
  0: 'status-badge bg-slate-100 text-slate-600',
  1: 'status-badge bg-emerald-100 text-emerald-700',
  2: 'status-badge bg-amber-100 text-amber-700'
}[status] || 'status-badge bg-slate-100 text-slate-600')

// ==================== 图片URL处理 ====================
const getImageUrl = (url) => {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `${process.env.VUE_APP_API_BASE_URL || ''}/${url}`.replace(/\/+/g, '/').replace(':/', '://')
}

// ==================== 资讯表单弹窗 ====================
const showFormModal = ref(false)
const isEdit = ref(false)
const coverInput = ref(null)
const pendingCoverFile = ref(null)
const coverPreviewUrl = ref('')
const showCoverPreview = ref(false)
const coverModified = ref(false)
const articleForm = reactive({
  id: null, categoryId: '', title: '', author: '', summary: '', content: '', coverImage: ''
})

const coverDisplayUrl = computed(() => {
  if (coverPreviewUrl.value) return coverPreviewUrl.value
  if (articleForm.coverImage) return getImageUrl(articleForm.coverImage)
  return ''
})

const openFormModal = async (item) => {
  pendingCoverFile.value = null
  coverPreviewUrl.value = ''
  coverModified.value = false
  if (item) {
    isEdit.value = true
    try {
      const data = await getArticleDetail(item.id)
      Object.assign(articleForm, {
        id: data.id,
        categoryId: data.categoryId || '',
        title: data.title,
        author: data.author || '',
        summary: data.summary || '',
        content: data.content || '',
        coverImage: data.coverImage || ''
      })
    } catch (error) {
      ElMessage.error(error.message || '加载资讯详情失败')
      return
    }
  } else {
    isEdit.value = false
    Object.assign(articleForm, {
      id: null,
      categoryId: selectedCategoryId.value || '',
      title: '', author: '', summary: '', content: '', coverImage: ''
    })
  }
  showFormModal.value = true
}

const closeFormModal = () => {
  showFormModal.value = false
  if (coverPreviewUrl.value) {
    URL.revokeObjectURL(coverPreviewUrl.value)
    coverPreviewUrl.value = ''
  }
  pendingCoverFile.value = null
}

const triggerCoverUpload = () => { coverInput.value?.click() }

const handleCoverUpload = (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    event.target.value = ''
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    ElMessage.warning('图片大小不能超过5MB')
    event.target.value = ''
    return
  }
  pendingCoverFile.value = file
  coverModified.value = true
  if (coverPreviewUrl.value) URL.revokeObjectURL(coverPreviewUrl.value)
  coverPreviewUrl.value = URL.createObjectURL(file)
  articleForm.coverImage = ''
  event.target.value = ''
}

const removeCover = () => {
  articleForm.coverImage = ''
  pendingCoverFile.value = null
  coverModified.value = true
  if (coverPreviewUrl.value) {
    URL.revokeObjectURL(coverPreviewUrl.value)
    coverPreviewUrl.value = ''
  }
}

const submitArticle = async () => {
  if (!articleForm.title.trim()) { ElMessage.warning('请输入资讯标题'); return }
  if (!articleForm.content || articleForm.content === '<p></p>') { ElMessage.warning('请输入资讯内容'); return }

  try {
    let coverImage = articleForm.coverImage
    if (pendingCoverFile.value) {
      const res = await uploadFile(pendingCoverFile.value, 'article', 'public')
      const fileKey = res?.fileName || res?.data?.fileName
      if (!fileKey) {
        ElMessage.error('封面上传失败，未获取到图片地址')
        return
      }
      coverImage = fileKey
    }

    const payload = {
      categoryId: articleForm.categoryId || undefined,
      title: articleForm.title,
      author: articleForm.author,
      summary: articleForm.summary,
      content: articleForm.content,
    }
    if (!isEdit.value || coverModified.value) {
      payload.coverImage = coverImage
    }
    if (isEdit.value) {
      await updateArticle(articleForm.id, payload)
      ElMessage.success('资讯更新成功')
    } else {
      await createArticle(payload)
      ElMessage.success('资讯创建成功')
    }
    closeFormModal()
    await loadArticleList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '保存资讯失败')
  }
}

// ==================== 资讯详情弹窗 ====================
const showDetailModal = ref(false)
const detailData = ref(null)
const detailLoading = ref(false)

const openDetailModal = async (item) => {
  showDetailModal.value = true
  detailLoading.value = true
  try {
    detailData.value = await getArticleDetail(item.id)
  } catch (error) {
    ElMessage.error(error.message || '加载详情失败')
  } finally {
    detailLoading.value = false
  }
}

// ==================== 操作 ====================
const handlePublish = async (item) => {
  openMoreId.value = null
  try {
    await publishArticle(item.id)
    ElMessage.success('发布成功')
    await loadArticleList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '发布失败')
  }
}

const handleOffline = async (item) => {
  openMoreId.value = null
  try {
    await offlineArticle(item.id)
    ElMessage.success('已下架')
    await loadArticleList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '下架失败')
  }
}

const handleOnline = async (item) => {
  openMoreId.value = null
  try {
    await onlineArticle(item.id)
    ElMessage.success('上架成功')
    await loadArticleList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '上架失败')
  }
}

const handleToggleTop = async (item) => {
  openMoreId.value = null
  try {
    await toggleArticleTop(item.id)
    ElMessage.success(item.isTop ? '已取消置顶' : '已置顶')
    await loadArticleList(pageInfo.current)
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// ==================== 更多菜单 ====================
const openMoreId = ref(null)
const openMoreItem = ref(null)
const moreMenuPos = reactive({ top: 0, left: 0 })

const moreMenuStyle = computed(() => ({
  top: `${moreMenuPos.top}px`,
  left: `${moreMenuPos.left}px`
}))

const toggleMoreMenu = (id, event) => {
  if (openMoreId.value === id) {
    openMoreId.value = null
    openMoreItem.value = null
  } else {
    const item = articleList.value.find(a => a.id === id)
    openMoreItem.value = item
    openMoreId.value = id
    const btn = event.currentTarget
    const rect = btn.getBoundingClientRect()
    moreMenuPos.top = rect.bottom + 4
    moreMenuPos.left = rect.right - 160
  }
}

const handleClickOutside = (e) => {
  if (openMoreId.value && !e.target.closest('.more-menu-container')) {
    openMoreId.value = null
    openMoreItem.value = null
  }
}

// ==================== 删除确认 ====================
const showConfirmModal = ref(false)
const confirmMessage = ref('')
const confirmPayload = ref({ type: '', id: null })

const confirmDeleteCategory = (node) => {
  confirmPayload.value = { type: 'category', id: node.id }
  confirmMessage.value = `确认删除分类「${node.name}」？分类下的子分类和资讯将无法访问。`
  showConfirmModal.value = true
}

const confirmDeleteArticle = (item) => {
  openMoreId.value = null
  confirmPayload.value = { type: 'article', id: item.id }
  confirmMessage.value = `确认删除资讯「${item.title}」？`
  showConfirmModal.value = true
}

const confirmDeleteAction = async () => {
  const { type, id } = confirmPayload.value
  try {
    if (type === 'category') {
      await deleteArticleCategory(id)
      ElMessage.success('分类已删除')
      await loadCategories()
    } else {
      await deleteArticle(id)
      ElMessage.success('资讯已删除')
      await loadArticleList(pageInfo.current)
    }
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  } finally {
    showConfirmModal.value = false
  }
}

// ==================== 初始化 ====================
onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  await loadCategories()
  await loadArticleList(1)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* 分类弹窗过渡动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-active > div:last-child > div,
.modal-leave-active > div:last-child > div {
  transition: transform 0.3s ease, opacity 0.25s ease;
}
.modal-enter-from > div:last-child > div {
  transform: translateY(16px);
  opacity: 0;
}
.modal-leave-to > div:last-child > div {
  transform: translateY(8px);
  opacity: 0;
}
</style>
